import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { LocalizationKeys } from 'assets/localizations/localizationKeys';
import { observer } from 'mobx-react';
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { billingApi, BillingSession } from 'services/businessBilling';
import { sessionErrorState } from 'services/businessBilling/restore';
import { useStores } from 'stores/storeInitializer';
import B2bBillingDetails from './B2bBillingDetails';
import { getErrorDisplay } from './components/BusinessError/errorDisplay';
import { ErrorActions } from './components/ErrorActions';
import ProductionBillingDetails from './production/ProductionBillingDetails';
import PolicyBillingDetails from './policy/PolicyBillingDetails';
import { parsePolicyError, parsePolicySession } from 'services/billingPolicy/validation';

type SessionState =
    | { key: string; session: BillingSession; error?: never }
    | { key: string; error: unknown; session?: never };

/** The saved server contract selects the form. Country, tenant settings and client flags cannot opt a journey in. */
export const PaymentTerminalBillingDetailsPage: React.FC = observer(() => {
    const { tenantId, token } = useParams<{ tenantId: string; token: string }>();
    const { search } = useLocation();
    const params = React.useMemo(() => new URLSearchParams(search), [search]);
    const verificationToken = params.get('verificationToken');
    const locale = params.get('locale');
    const { store } = useStores();
    const [attempt, setAttempt] = React.useState(0);
    const key = JSON.stringify([tenantId, token, verificationToken, attempt]);
    const [loaded, setLoaded] = React.useState<SessionState | null>(null);
    const request = React.useRef<{ key: string; promise: Promise<BillingSession> }>();

    React.useLayoutEffect(() => {
        if (tenantId && token) {
            store.setLanguageContext(`billing-details:${tenantId}:${token}`);
        }
    }, [store, tenantId, token]);

    React.useEffect(() => {
        store.applySuggestedLanguage(locale);
    }, [store, locale]);

    React.useEffect(() => {
        if (!tenantId || !token) {
            return;
        }
        let active = true;
        // Reuse the same request when React replays an effect. Children never fetch a second session for selection.
        if (request.current?.key !== key) {
            request.current = { key, promise: billingApi.loadSession(tenantId, token, verificationToken) };
        }
        request.current.promise.then(
            (session) => { if (active) setLoaded({ key, session }); },
            (error) => { if (active) setLoaded({ key, error }); },
        );
        return () => { active = false; };
    }, [key, tenantId, token, verificationToken]);

    const current = loaded?.key === key ? loaded : null;
    const session = current?.session;
    const validSession = session && typeof session === 'object' && !Array.isArray(session)
        && session.token === token && typeof session.status === 'string' && session.status.length > 0;
    if (validSession && (session.billingContract === undefined || session.billingContract === 'legacy')) {
        return <ProductionBillingDetails key={key} sessionContext={session} />;
    }
    if (validSession && session.billingContract === 'romanian-business-v1') {
        return <B2bBillingDetails key={key} sessionContext={session} />;
    }
    if (validSession && session.billingContract === 'billing-policy-v1' && tenantId && verificationToken) {
        const enhanced = parsePolicySession(session, token!);
        if (enhanced) return <PolicyBillingDetails key={key} session={enhanced} tenantId={tenantId} verificationToken={verificationToken} />;
    }

    const loading = !current && Boolean(tenantId && token);
    const failed = current !== null && 'error' in current;
    const httpFailure = failed ? (current.error as { response?: { status?: number; data?: unknown } })?.response : null;
    const enhancedError = parsePolicyError(httpFailure?.data);
    const enhancedAccessError = httpFailure?.status === 401 && enhancedError?.code === 'BILLING_SESSION_UNAUTHORIZED'
        ? LocalizationKeys.BusinessErrorSessionUnauthorized
        : httpFailure?.status === 410 && enhancedError?.code === 'BILLING_SESSION_EXPIRED' ? LocalizationKeys.PolicyExpired : null;
    const restoredError = failed ? sessionErrorState(current.error) : null;
    const sessionFailure = restoredError?.kind === 'failure' && restoredError.response.outcome === 'session_error'
        ? restoredError.response
        : null;
    const failureDisplay = sessionFailure ? getErrorDisplay({ kind: 'code', code: sessionFailure.error.code, field: null }) : null;

    return (
        <Box p={3} maxWidth={520} mx="auto" display="flex" flexDirection="column" gap={2}>
            <Typography variant="h5">{store.L(LocalizationKeys.BillingDetailsTitle)}</Typography>
            {loading ? (
                <CircularProgress aria-label={store.L(LocalizationKeys.BillingDetailsLoading)} />
            ) : (
                <>
                    <Alert severity="error">
                        {store.L(enhancedAccessError || failureDisplay?.messageKey || (failed
                            ? LocalizationKeys.BillingDetailsSessionUnavailable
                            : LocalizationKeys.BillingDetailsContractUnsupported))}
                    </Alert>
                    {sessionFailure || enhancedAccessError ? (
                        <ErrorActions actions={['RESTART_FROM_TERMINAL']} retryAvailableAt={null} retriesLeft={0}
                            onEditCui={() => {}} onRetry={() => {}}
                            onSwitchToIndividual={() => {}} onContinueWithout={() => {}} />
                    ) : failed ? (
                        <Button onClick={() => setAttempt((value) => value + 1)}>
                            {store.L(LocalizationKeys.BusinessActionRetry)}
                        </Button>
                    ) : null}
                </>
            )}
        </Box>
    );
});

export default PaymentTerminalBillingDetailsPage;
